import { cx } from "@/utils/cx";
import { formatChangeMagnitude } from "../lib/format";
import type { ChangeUnit } from "../lib/metrics";
import { regionCode, regionName } from "../lib/regions";

const ArrowIcon = ({ up }: { up: boolean }) => (
  <svg viewBox="0 0 12 12" width={11} height={11} aria-hidden="true" className="shrink-0">
    <path
      d={up ? "M3.5 8.5l5-5M4.5 3.5h4v4" : "M3.5 3.5l5 5M8.5 4.5v4h-4"}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LIGHT_PILL = { up: "bg-[#ecfdf3] text-[#067647]", down: "bg-[#fef3f2] text-[#b42318]" };
const DARK_PILL = { up: "bg-[#079455]/20 text-[#75e0a7]", down: "bg-[#d92d20]/20 text-[#fda29b]" };

export const ChangePill = ({
  change,
  unit = "percent",
  title,
  tone = "light",
}: {
  change: number | null;
  unit?: ChangeUnit;
  title?: string;
  tone?: "light" | "dark";
}) => {
  if (change === null) return null;
  const up = change >= 0;
  const palette = tone === "dark" ? DARK_PILL : LIGHT_PILL;

  return (
    <span
      title={title}
      className={cx(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] leading-[18px] font-medium tabular-nums",
        up ? palette.up : palette.down,
      )}
    >
      <ArrowIcon up={up} />
      <span className="sr-only">{up ? "up" : "down"}</span>
      {formatChangeMagnitude(change, unit)}
    </span>
  );
};

export const ChangeLine = ({ change, unit = "percent" }: { change: number | null; unit?: ChangeUnit }) => {
  if (change === null) return null;
  const up = change >= 0;

  return (
    <span className="inline-flex items-center gap-[3px] text-[13px] leading-[18px] text-[#21133a]/60">
      <span className={up ? "text-[#079455]" : "text-[#d92d20]"}>
        <ArrowIcon up={up} />
      </span>
      <span className="sr-only">{up ? "up" : "down"}</span>
      {formatChangeMagnitude(change, unit)}
    </span>
  );
};

export const RegionBadge = ({ regionKey, tone = "light" }: { regionKey: string; tone?: "light" | "dark" }) => (
  <span
    title={regionName(regionKey)}
    className={cx(
      "shrink-0 rounded-full px-[7px] text-[11px] leading-[18px] font-medium",
      tone === "dark" ? "bg-[#c0a7f2]/15 text-[#c0a7f2]" : "border border-[#21133a]/15 bg-white text-[#21133a]",
    )}
  >
    {regionCode(regionKey)}
  </span>
);

const SPARK_WIDTH = 200;
const SPARK_HEIGHT = 36;

export const Sparkline = ({ points }: { points: number[] }) => {
  if (points.length < 2) return <div className="h-9" />;

  const max = Math.max(...points);
  const min = Math.min(...points);
  const spread = max - min || 1;
  const coordinates = points.map((value, index) => [
    (index * SPARK_WIDTH) / (points.length - 1),
    SPARK_HEIGHT - 3 - ((value - min) / spread) * (SPARK_HEIGHT - 6),
  ]);
  const path = coordinates.map(([x, y], index) => `${index ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const [lastX, lastY] = coordinates[coordinates.length - 1];

  return (
    <div className="relative h-9 w-full">
      <svg
        viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
        preserveAspectRatio="none"
        className="h-full w-full"
        aria-hidden="true"
      >
        <path
          d={path}
          fill="none"
          stroke="rgba(33,19,58,0.5)"
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <span
        aria-hidden="true"
        className="absolute size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#21133a]"
        style={{ left: `${(lastX / SPARK_WIDTH) * 100}%`, top: `${(lastY / SPARK_HEIGHT) * 100}%` }}
      />
    </div>
  );
};
